<template>
  <!-- Print page - completely standalone without any layout -->
  <template v-if="isPrintPage">
    <router-view/>
  </template>

  <!-- Normal app layout -->
  <template v-else>
    <!-- Show DashboardSelection if no system is chosen -->
    <DashboardSelection v-if="!selectedSystem" @system-selected="handleSystemSelected" />
    <LicenseAgreement v-if="showLicenseAgreement" @agreement-complete="handleAgreementComplete" @cancel="handleAgreementCancel" />
    
    <!-- E-CAT System View -->
    <template v-else-if="selectedSystem === 'ecat'">
      <button class="menu-toggle" v-bind:class="{ 'menu-toggle-black': collapsed }" @click="toggleMenu()">
        <i v-if="collapsed" class="fas fa-bars"></i>
        <i v-if="!collapsed" class="fas fa-times"></i>
        <span>E-CAT</span>
      </button>
      <nav-bar 
        ref="nav"
        :collapsed="collapsed"
        @on-item-clicked="setCollapsed"
        @request-system-change="resetSystemSelection"
      />
      <router-view/>
      <!-- PWA components (specific to E-CAT or global) -->
      <PWAUpdatePopup />
      <PWAInstallPrompt />
    </template>

    <template v-else-if="selectedSystem == 'rcat'">
      <button class="menu-toggle" v-bind:class="{ 'menu-toggle-black': collapsed }" @click="toggleMenu()">
        <i v-if="collapsed" class="fas fa-bars"></i>
        <i v-if="!collapsed" class="fas fa-times"></i>
        <span>R-CAT</span>
      </button>

      <nav-bar 
        ref="nav"
        :collapsed="collapsed"
        @on-item-clicked="setCollapsed"
        @request-system-change="resetSystemSelection"
      />

      <router-view/>

      <div class="beta-ribbon">BETA</div>
    </template>
  </template>
</template>

<script>
import NavBar from './components/NavBar';
import PWAUpdatePopup from './components/pwa/pwa-update-popup.vue';
import PWAInstallPrompt from './components/pwa/pwa-install-prompt.vue';
import { initPWA } from './services/pwa.service';
import DashboardSelection from './components/DashboardSelection.vue'; 
import LicenseAgreement from '@/components/LicenseAgreement.vue';

export default {
  components: {
    "nav-bar": NavBar,
    PWAUpdatePopup,
    PWAInstallPrompt,
    DashboardSelection,
    LicenseAgreement
  },
  data() {
    return {
      collapsed: true,
      selectedSystem: null,
      showLicenseAgreement: false,
    }
  },
  computed: {
    // Check if current route is the print page
    isPrintPage() {
      return this.$route.name === 'SurveyPrint';
    },
    shouldShowLicenseAgreement() {
      // Check if the user needs to agree to the license
      if (this.user) {
        return !this.user.agreedTcs;
      } else {
        return localStorage.getItem("@license-agreed") !== "true";
      }
    }
  },
  methods: {
    checkLicenseAgreement() {
      // If we're on a route that requires agreement, check if user has agreed
      this.showLicenseAgreement = this.shouldShowLicenseAgreement;
    },
    handleAgreementComplete() {
      this.showLicenseAgreement = false;
    },
    handleAgreementCancel() {
      this.showLicenseAgreement = false;
    },
    setCollapsed($event) {
      this.collapsed = $event;
    },
    toggleMenu() {
      this.collapsed = !this.collapsed
      // if (this.$refs.nav) { 
      //    this.collapsed = this.$refs.nav.toggleMenu();
      // }
    },
    handleSystemSelected(system) {
      const previousSystem = this.selectedSystem;
      this.selectedSystem = system;
      localStorage.setItem('@app-system', system);
      
      // Initialize PWA features if E-CAT is selected (and wasn't already active)
      if (system === 'ecat' && previousSystem !== 'ecat') {
        initPWA();
      }
    },
    resetSystemSelection() {
      this.selectedSystem = null;
      localStorage.removeItem('@app-system');
      if (this.$route.path !== '/') {
        this.$router.push('/');
      }
    }
  },
  created() {
    this.checkLicenseAgreement();

    // Load system choice from localStorage when the app is created
    const savedSystem = localStorage.getItem('@app-system');
    if (savedSystem) {
      this.selectedSystem = savedSystem;
    }
  },
  mounted() {
    // If E-CAT was loaded from localStorage, ensure PWA is initialized
    if (this.selectedSystem === 'ecat') {
      initPWA();
    }
    // Note: The menu-toggle and nav-bar are part of the E-CAT system,
    // so their related logic (`this.$refs.nav`) will only be relevant
    // when `selectedSystem === 'ecat'`.
  }
}
</script>

<!-- Global CSS styles (unchanged from your original) -->
<style>
  #app {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #2c3e50;
  }

  html, body, div, span, applet, object, iframe,
  h1, h2, h3, h4, h5, h6, p, blockquote, pre,
  a, abbr, acronym, address, big, cite, code,
  del, dfn, em, img, ins, kbd, q, s, samp,
  small, strike, strong, sub, sup, tt, var,
  b, u, i, center,
  dl, dt, dd, ol, ul, li,
  fieldset, form, label, legend,
  table, caption, tbody, tfoot, thead, tr, th, td,
  article, aside, canvas, details, embed, 
  figure, figcaption, footer, header, hgroup, 
  menu, nav, output, ruby, section, summary,
  time, mark, audio, video {
    margin: 0;
    padding: 0;
    border: 0;
    font-size: 100%;
    font: inherit;
    vertical-align: baseline;
  }
  /* HTML5 display-role reset for older browsers */
  article, aside, details, figcaption, figure, 
  footer, header, hgroup, menu, nav, section {
    display: block;
  }
  body {
    line-height: 1;
  }
  ol, ul {
    list-style: none;
  }
  blockquote, q {
    quotes: none;
  }
  blockquote:before, blockquote:after,
  q:before, q:after {
    content: '';
    content: none;
  }
  table {
    border-collapse: collapse;
    border-spacing: 0;
  }

  .grecaptcha-badge {
    right: 0 !important;
    top: 0 !important;
    left: auto !important;
    bottom: auto !important;
    width: 70px !important;
    overflow: hidden !important;
  }

  @media (max-width: 768px) {
  .grecaptcha-badge {
    right: 0 !important;
    bottom: 0 !important;
    top: auto !important;
    left: auto !important;
  }
}

/* Styles for R-CAT Placeholder (can be moved to a separate component if it grows) */
.rcat-placeholder-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #e9ecef; /* A slightly different neutral background */
  padding: 20px;
  box-sizing: border-box;
  font-family: 'Source Sans Pro', sans-serif;
  color: #343a40;
  text-align: center;
}

.rcat-placeholder-content {
  background-color: #fff;
  padding: 30px 40px; /* Adjusted padding */
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  max-width: 550px; /* Adjusted max-width */
  width: 90%;
}

.platform-logo-placeholder {
  max-width: 120px;
  margin-bottom: 20px;
}

.rcat-placeholder-content h1 {
  font-size: 2.5rem; /* Adjusted */
  color: #0069d9; /* A slightly different blue for R-CAT */
  margin-bottom: 5px;
  font-weight: 700;
}

.system-subtitle-placeholder {
  font-size: 1.3rem; /* Adjusted */
  color: #495057;
  margin-bottom: 25px;
  font-weight: normal;
}

.rcat-placeholder-content p {
  font-size: 1.05rem; /* Adjusted */
  line-height: 1.6;
  margin-bottom: 15px;
}

.back-button-placeholder {
  background-color: #5a6268; /* Bootstrap secondary grey, slightly darker */
  color: white;
  border: none;
  padding: 12px 25px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin-top: 20px;
}

.back-button-placeholder:hover {
  background-color: #4e555b; /* Darker shade for hover */
}

@media print {
  .no-print {
    display: none !important;
  }

  
  .grecaptcha-badge {
    display: none !important;
  }
}

</style>

<!-- Global SCSS styles (unchanged from your original) -->
<style lang="scss">
  @import "/breakpoints.scss";

  * {
    box-sizing: border-box;
  }

  html, body {
    font-family: 'Source Sans Pro', sans-serif;
  }


  .page-wrapper {
    box-sizing: border-box;
    width: 100%;
    padding-top: 40px;

    @include md {
      padding-top: 0px;
    }

    .content-wrapper {
      .uni-logo {
        max-height: 60px;
      }

      @include md {
        max-width: 90%;
        margin: 0 auto;
      }
    }
  }

  .btn {
    cursor: pointer;
    background: #013B82;
    display: block;
    width: 100%;
    text-align: center;

    outline: 0;
    border: 0;
    color: #FFF;

    font-size: 18px;
    padding: 5px 8px;
    border-radius: 5px;
    text-decoration: none;

    &:hover {
      background-color: darken(#013B82, 10%) !important;
    }
    
  }

  .btn-delete {
    background: #C85250;
    display: block;
    width: 100%;

    outline: 0;
    border: 0;
    color: #FFF;

    font-size: 18px;
    padding: 5px 8px;
    border-radius: 5px;
    color: #FFF;
    text-decoration: none;
  }

  .btn-accent {
    background: #FFF;
    display: block;
    width: 100%;

    outline: 0;
    border: 1px solid #CECECE;
    color: #000;

    font-size: 18px;
    padding: 5px 8px;
    border-radius: 5px;
    text-decoration: none;

    &:hover {
      background-color: darken(#FFF, 10%) !important;
    }
  }

  form {
    .form-group {
      display: flex;
      flex-direction: column;
      text-align: left;
      margin-bottom: 10px;

      label {
        margin-bottom: 5px;
      }

      input, select {
        padding: 5px;
      }
    }
  }

  .input-picker {
    width: 100%;
    padding: 5px
  }

  .v-sidebar-menu {
    transition: 0.3s all;
    padding-top: 50px;

    @include md {
      padding-top: 0px;
    }
  }

  .v-sidebar-menu .vsm--title {
    font-family: 'Source Sans Pro', sans-serif !important;
  }

  .vsm--header {
    font-family: 'Source Sans Pro', sans-serif !important;
  }

  .menu-toggle {
    position: fixed;
    left: 0px;
    z-index: 9999;
    transition: 0.3s all;
    width: 100%;
    text-align: left;

    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 18px;
    color: #FFF;
    outline: 0;
    border: 0px;
    font-size: 32px;
    background: #2A2A2E;

    display: flex;
    align-items: center;

    @include md {
      display: none;
    }

    span {
      padding-left: 20px;
      font-size: 16px;
      font-weight: bold;

      font-family: 'Source Sans Pro', sans-serif;
    }
  }

  .menu-toggle-black {
    color: #000;
    background: #ededed;
  }

  .v-sidebar-menu.vsm_collapsed {
    transform: translateX(-100%);
    @include md {
      transform: translateX(-0%);
    }
  }

  
.beta-ribbon {
  position: fixed;
  bottom: 15px;
  left: -45px;
  z-index: 1001;
  background: linear-gradient(135deg, #FF4444, #CC0000);
  color: white;
  padding: 12px 60px;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  transform: rotate(45deg);  
  border: 2px solid #AA0000;
  
  .beta-text {
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  }
}
</style>